import { isValidCollegeContactEmail, isValidUsCollegeContactPhone } from "./contactFieldValidation";

export interface ListedContact {
    fullName: string;
    collegeName: string;
    title?: string;
    email?: string;
    phone?: string;
}

const NAME_LINE =
    /^\s*(?:(?:[-*]|\d+\.)\s+)?\*\*([^*]+)\*\*(?:\s*\(([^)]+)\))?\s*$/;
const PLAIN_NAME_LINE =
    /^\s*(?:[-*]|\d+\.)\s+([A-Z][A-Za-z.'-]+(?:\s+[A-Z][A-Za-z.'-]+)+)(?:\s*\(([^)]+)\))?\s*$/;
const FIELD_LINE = /^\s*(?:[-*]\s+)?(Title|Email|Phone)\s*:\s*(.+)\s*$/i;
const PERSON_NAME = /^[A-Za-z][A-Za-z.'-]+(?:\s+[A-Za-z][A-Za-z.'-]+)+$/;
const SCHOOL_HINT = /\b(university|college|institute|polytechnic)\b/i;
const OFFICE_HINT =
    /\b(athletics(?:\s+main\s+line)?|admissions(?:\s+office)?|athletic(?:s)?\s+department|coaching staff)\b/i;
const REJECTED_ROLE =
    /\b(?:campus\s+police|police(?:\s+department)?|public\s+safety|campus\s+safety|security(?:\s+office)?|emergency(?:\s+services)?|parking|dining|bookstore|housing|residence\s+life|health\s+center|counseling|help\s*desk|webmaster|it\s+support)\b/i;
const OFFICE_PHONE =
    /\b(?:the\s+)?([A-Z][A-Za-z.'-]+\s+(?:Athletics(?:\s+main\s+line)?|Admissions(?:\s+office)?|Athletic(?:s)?\s+Department|Coaching Staff))\s+(?:at|:)\s+(\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4})/g;
const NAMED_SCHOOL =
    /\b((?:[A-Z][A-Za-z0-9.'-]+(?:\s+[A-Z][A-Za-z0-9.'-]+){0,6})\s+(?:University|College|Institute|Polytechnic)|University\s+of\s+[A-Z][A-Za-z0-9.'-]+(?:\s+[A-Z][A-Za-z0-9.'-]+){0,4})\b/g;

function looksLikePersonName(name: string): boolean {
    return (
        PERSON_NAME.test(name) &&
        !SCHOOL_HINT.test(name) &&
        !isRejectedRecruitingRole(name) &&
        name.length >= 5 &&
        name.length <= 80
    );
}

function isRejectedRecruitingRole(value: string | undefined): boolean {
    return Boolean(value && REJECTED_ROLE.test(value));
}

function isRejectedContact(contact: ListedContact): boolean {
    const emailLocal = contact.email?.split("@")[0]?.replace(/[._-]/g, " ");
    return (
        isRejectedRecruitingRole(contact.fullName) ||
        isRejectedRecruitingRole(contact.title) ||
        isRejectedRecruitingRole(emailLocal)
    );
}

function looksLikeSchoolName(name: string): boolean {
    return name.length >= 4 && SCHOOL_HINT.test(name);
}

function looksLikeOfficeName(name: string): boolean {
    return OFFICE_HINT.test(name);
}

function splitOfficeLabel(label: string): { fullName: string; title?: string } {
    const trimmed = label.replace(/\s+/g, " ").trim();
    const mainLine = /^(.*?)\s+main line$/i.exec(trimmed);
    if (mainLine?.[1]) {
        return { fullName: mainLine[1].trim(), title: "Main line" };
    }
    return { fullName: trimmed };
}

function blankToUndefined(value: string | undefined): string | undefined {
    const trimmed = value?.trim();
    return trimmed ? trimmed : undefined;
}

function firstSchoolName(...texts: Array<string | undefined>): string | undefined {
    for (const text of texts) {
        if (!text) {
            continue;
        }
        NAMED_SCHOOL.lastIndex = 0;
        const match = NAMED_SCHOOL.exec(text);
        if (match?.[1] && looksLikeSchoolName(match[1])) {
            return match[1].trim().replace(/\s+/g, " ");
        }
    }
    return undefined;
}

function parseFollowingFields(
    lines: string[],
    start: number,
): { title?: string; email?: string; phone?: string; next: number } {
    let title: string | undefined;
    let email: string | undefined;
    let phone: string | undefined;
    let j = start;
    while (j < lines.length) {
        const line = lines[j];
        if (!line.trim()) {
            j += 1;
            continue;
        }
        if (NAME_LINE.test(line) || PLAIN_NAME_LINE.test(line) || /^\s*(?:[-*]|\d+\.)\s+\*\*/.test(line)) {
            break;
        }
        const field = FIELD_LINE.exec(line);
        if (!field) {
            break;
        }
        const value = field[2].trim();
        if (field[1].toLowerCase() === "title") {
            title = value;
        } else if (field[1].toLowerCase() === "email") {
            email = value;
        } else if (field[1].toLowerCase() === "phone") {
            phone = value;
        }
        j += 1;
    }
    return { title, email, phone, next: j };
}

export function extractListedContacts(
    markdown: string,
    limit = 8,
    collegeHint?: string,
): ListedContact[] {
    if (!markdown) {
        return [];
    }
    const fallbackCollege = firstSchoolName(collegeHint, markdown);
    const lines = markdown.split(/\r?\n/);
    const found: ListedContact[] = [];
    const seen = new Set<string>();

    for (let i = 0; i < lines.length; i += 1) {
        const bold = NAME_LINE.exec(lines[i]);
        const plain = bold ? null : PLAIN_NAME_LINE.exec(lines[i]);
        const match = bold ?? plain;
        if (!match) {
            continue;
        }
        const fullName = match[1].trim().replace(/\s+/g, " ");
        if (!looksLikePersonName(fullName)) {
            continue;
        }
        const paren = match[2]?.trim().replace(/\s+/g, " ");
        let collegeName = paren && looksLikeSchoolName(paren.replace(/^College:\s*/i, ""))
            ? paren.replace(/^College:\s*/i, "").trim()
            : fallbackCollege;
        let titleFromParen = paren && !looksLikeSchoolName(paren.replace(/^College:\s*/i, ""))
            ? paren
            : undefined;
        if (!collegeName) {
            continue;
        }

        const fields = parseFollowingFields(lines, i + 1);
        const contact: ListedContact = {
            fullName,
            collegeName,
            title: blankToUndefined(fields.title ?? titleFromParen),
            email: blankToUndefined(fields.email),
            phone: blankToUndefined(fields.phone),
        };
        if (!isValidListedContact(contact)) {
            continue;
        }
        const key = `${fullName.toLowerCase()}|${collegeName.toLowerCase()}|${(contact.email ?? "").toLowerCase()}`;
        if (seen.has(key)) {
            continue;
        }
        seen.add(key);
        found.push(contact);
        if (found.length >= limit) {
            break;
        }
    }
    return found;
}

export function extractOfficeContactsFromProse(
    markdown: string,
    collegeHint?: string,
): ListedContact[] {
    if (!markdown) {
        return [];
    }
    const collegeName = firstSchoolName(collegeHint, markdown);
    if (!collegeName) {
        return [];
    }
    const found: ListedContact[] = [];
    const seen = new Set<string>();
    OFFICE_PHONE.lastIndex = 0;
    let match = OFFICE_PHONE.exec(markdown);
    while (match) {
        const office = splitOfficeLabel(match[1].replace(/\s+/g, " ").trim());
        const contact: ListedContact = {
            fullName: office.fullName,
            collegeName,
            title: office.title,
            phone: match[2].trim(),
        };
        if (isValidListedContact(contact)) {
            const key = `${contact.fullName.toLowerCase()}|${(contact.phone ?? "").replace(/\D/g, "")}`;
            if (!seen.has(key)) {
                seen.add(key);
                found.push(contact);
            }
        }
        match = OFFICE_PHONE.exec(markdown);
    }
    return found;
}

export function extractAllAssistContacts(
    markdown: string,
    limit = 8,
    collegeHint?: string,
): ListedContact[] {
    const listed = extractListedContacts(markdown, limit, collegeHint);
    const offices = extractOfficeContactsFromProse(markdown, collegeHint);
    const seen = new Set(
        listed.map(
            (contact) =>
                `${contact.fullName.toLowerCase()}|${(contact.phone ?? "").replace(/\D/g, "")}|${(contact.email ?? "").toLowerCase()}`,
        ),
    );
    const merged = [...listed];
    for (const office of offices) {
        const key = `${office.fullName.toLowerCase()}|${(office.phone ?? "").replace(/\D/g, "")}|${(office.email ?? "").toLowerCase()}`;
        if (seen.has(key)) {
            continue;
        }
        seen.add(key);
        merged.push(office);
        if (merged.length >= limit) {
            break;
        }
    }
    return merged;
}

export function findContactForCollege(markdown: string, collegeName: string): ListedContact | undefined {
    const wanted = collegeName.trim().toLowerCase();
    return extractAllAssistContacts(markdown, 50, collegeName).find((contact) => {
        const name = contact.collegeName.trim().toLowerCase();
        return name === wanted || name.includes(wanted) || wanted.includes(name);
    });
}

export function isValidListedContact(contact: ListedContact): boolean {
    if (isRejectedContact(contact)) {
        return false;
    }
    const usableName =
        looksLikePersonName(contact.fullName) || looksLikeOfficeName(contact.fullName);
    if (!usableName || !looksLikeSchoolName(contact.collegeName)) {
        return false;
    }
    const email = contact.email?.trim() ?? "";
    const phone = contact.phone?.trim() ?? "";
    if (!email && !phone) {
        return false;
    }
    return isValidCollegeContactEmail(email) && isValidUsCollegeContactPhone(phone);
}
